import type {
  ApiError,
  CatalogItemData,
  CatalogItemFields,
  CreateQuoteInput,
  QuoteEditableFields,
  QuoteEvent,
  QuoteStatus,
  QuoteView,
} from "@contracts/index";

/** Thin fetch wrapper for the API. Throws ApiRequestError with the server's code and message. */
export class ApiRequestError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = "ApiRequestError";
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`/api${path}`, {
    ...init,
    credentials: "same-origin",
    headers: { "Content-Type": "application/json", ...(init?.headers ?? {}) },
  });
  if (!response.ok) {
    let payload: ApiError | null = null;
    try {
      payload = (await response.json()) as ApiError;
    } catch {
      // body was not JSON; fall through with a generic message
    }
    throw new ApiRequestError(
      response.status,
      payload?.error.code ?? "http_error",
      payload?.error.message ?? response.statusText,
    );
  }
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

const post = <T>(path: string, body?: unknown) =>
  request<T>(path, { method: "POST", body: body === undefined ? undefined : JSON.stringify(body) });
const put = <T>(path: string, body: unknown) => request<T>(path, { method: "PUT", body: JSON.stringify(body) });

export const api = {
  health: () => request<{ ok: boolean; storage: string }>("/health"),
  admin: {
    listQuotes: (status?: QuoteStatus) => request<QuoteView[]>(`/manage/quotes${status ? `?status=${status}` : ""}`),
    getQuote: (id: string) => request<QuoteView>(`/manage/quotes/${id}`),
    createQuote: (input: CreateQuoteInput) => post<QuoteView>("/manage/quotes", input),
    updateQuote: (id: string, fields: QuoteEditableFields) => put<QuoteView>(`/manage/quotes/${id}`, fields),
    duplicateQuote: (id: string) => post<QuoteView>(`/manage/quotes/${id}/duplicate`),
    cancelQuote: (id: string) => post<QuoteView>(`/manage/quotes/${id}/cancel`),
    sendQuote: (id: string) => post<QuoteView>(`/manage/quotes/${id}/send`),
    resendQuote: (id: string) => post<QuoteView>(`/manage/quotes/${id}/resend`),
    quotePdfUrl: (id: string) => `/api/manage/quotes/${id}/pdf`,
    quoteEvents: (id: string) => request<QuoteEvent[]>(`/manage/quotes/${id}/events`),
    listCatalog: (activeOnly = false) => request<CatalogItemData[]>(`/manage/catalog${activeOnly ? "?active=true" : ""}`),
    createCatalogItem: (fields: CatalogItemFields) => post<CatalogItemData>("/manage/catalog", fields),
    updateCatalogItem: (id: string, fields: CatalogItemFields) => put<CatalogItemData>(`/manage/catalog/${id}`, fields),
  },
};

export function errorMessage(error: unknown): string {
  if (error instanceof ApiRequestError) return error.message;
  return error instanceof Error ? error.message : String(error);
}

/** Static Web Apps exposes the signed-in user at /.auth/me (null when anonymous). */
export interface SwaUser {
  userDetails: string;
  userRoles: string[];
}

export async function currentUser(): Promise<SwaUser | null> {
  try {
    const response = await fetch("/.auth/me");
    if (!response.ok) return null;
    const payload = (await response.json()) as { clientPrincipal: SwaUser | null };
    return payload.clientPrincipal;
  } catch {
    return null;
  }
}
